import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  sleepPhasesSeries,
  sleepSoundEvents,
  sleepDayStats,
  weekOverview,
  sensors,
  ambientNoiseSeries,
  ambientNoise,
  breathFrequency,
} from "../mock";
import { useMic } from "../context/MicContext";
import { useJournal } from "../context/JournalContext";
import { useAuth } from "../context/AuthContext";
import { playClipBlob, stopClip } from "../services/clipPlayer";
import { generateTips } from "../lib/tips";
import SensorIcon from "../components/SensorIcon";
import InfoPoint from "../components/InfoPoint";
import AudioTimeline from "../components/AudioTimeline";
import ClipConsentDialog from "../components/ClipConsentDialog";
import { useAudioClips } from "../context/AudioClipsContext";
import {
  Upload,
  Mic,
  MicOff,
  HeartPulse,
  Moon,
  AlarmClock,
  Activity,
  Timer,
  Smile,
  BedDouble,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Trash2,
  Heart,
  Wind,
  MapPin,
  ChevronRight,
  Plus,
  Calendar,
  AudioLines,
  Waves,
  Lightbulb,
  Coffee,
  Wine,
  Brain,
  Dumbbell,
  Notebook,
  Sparkles,
  AlertCircle,
  Volume2,
  Loader2,
} from "lucide-react";

// Mappa nome fase backend → indice numerico per PhasesChart (0=DEEP, 1=LIGHT, 2=REM, 3=WAKE)
const PHASE_TO_IDX = { DEEP: 0, LIGHT: 1, REM: 2, WAKE: 3 };

// Empty-state constants: quando l'utente non ha dati reali mostriamo le
// stesse sezioni con valori a zero / dash, MAI dati mock inventati.
const EMPTY_DAY_STATS = {
  date: "—",
  quality: 0,
  scoreMattress: 0,
  scoreIntegrated: 0,
  inBed: "0h 00m",
  asleep: "0h 00m",
  regularity: 0,
  fellAsleepIn: "—",
  bedtime: "—",
  wakeup: "—",
  snoring: "0 min",
  clips: { done: 0, total: 0 },
  mood: "—",
  heartRateAvg: 0,
  coughPerHour: 0,
  coughLabel: "—",
  breathInterruptionsPerHour: 0,
  breathInterruptionsLabel: "—",
};
const EMPTY_WEEK = [
  { d: "L", label: "Lun", quality: 0 },
  { d: "M", label: "Mar", quality: 0 },
  { d: "M", label: "Mer", quality: 0 },
  { d: "G", label: "Gio", quality: 0 },
  { d: "V", label: "Ven", quality: 0 },
  { d: "S", label: "Sab", quality: 0 },
  { d: "D", label: "Dom", quality: 0 },
];
const EMPTY_PHASES_MIN = { deep_min: 0, light_min: 0, rem_min: 0, awake_min: 0 };
const EMPTY_BREATH = { median: 0, label: "—", unit: "/min" };
const EMPTY_AMBIENT = { median: 0, label: "—", unit: "dB" };

// Formatta minuti (totale int o float) come "Xh YYm"
function formatHM(totalMin) {
  if (totalMin == null) return "—";
  const m = Math.max(0, Math.round(Number(totalMin) || 0));
  const h = Math.floor(m / 60);
  const r = m % 60;
  return `${h}h ${String(r).padStart(2, "0")}m`;
}

// Calcola mediana
function median(values) {
  if (!values.length) return null;
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 === 1 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

// ---------- Week rings ----------
function DayRing({ d, quality, active }) {
  const size = 44;
  const r = (size - 4) / 2;
  const c = 2 * Math.PI * r;
  const dash = (c * quality) / 100;
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            stroke="rgba(255,255,255,0.08)"
            strokeWidth="3"
            fill="none"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            stroke={active ? "#14c2a1" : "rgba(20,194,161,0.55)"}
            strokeWidth="3"
            fill="none"
            strokeDasharray={`${dash} ${c}`}
            strokeLinecap="round"
          />
        </svg>
        <div
          className={`absolute inset-0 flex items-center justify-center text-[13px] font-medium ${
            active ? "text-white" : "text-white/70"
          }`}
        >
          {d}
        </div>
      </div>
    </div>
  );
}

// ---------- Quality gauge (SmartDream teal) ----------
function QualityGauge({ value = 84, size = 140 }) {
  const r = (size - 14) / 2;
  const c = 2 * Math.PI * r;
  const dash = (c * value) / 100;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <defs>
          <linearGradient id="qg" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#18d2ae" />
            <stop offset="100%" stopColor="#0a8670" />
          </linearGradient>
        </defs>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke="rgba(255,255,255,0.08)"
          strokeWidth="10"
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke="url(#qg)"
          strokeWidth="10"
          fill="none"
          strokeDasharray={`${dash} ${c}`}
          strokeLinecap="round"
          style={{
            filter: "drop-shadow(0 0 10px rgba(20,194,161,0.45))",
          }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-white text-[36px] leading-none font-semibold tracking-tight">
          {value}
          <span className="text-[14px] align-top ml-0.5 opacity-80">%</span>
        </div>
        <div className="text-white/70 text-[11px] uppercase tracking-wider mt-1 flex items-center gap-1">
          Qualità <ChevronRight size={10} />
        </div>
      </div>
    </div>
  );
}

// ---------- Sleep phases chart ----------
function PhasesChart({ series, events = [], selectedEventIdx = 0, onSelect }) {
  const isEmpty = !series || series.length === 0;
  const w = 620;
  const h = 180;
  const padX = 12;
  const padY = 24;
  const innerW = w - padX * 2;
  const innerH = h - padY * 2;

  const points = useMemo(() => {
    return series.map((v, i) => {
      const x = padX + (i / (series.length - 1)) * innerW;
      // map 0 (deep) → bottom (y high), 3 (awake) → top (y low)
      const y = padY + innerH - (v / 3) * innerH;
      return [x, y];
    });
  }, [series]);

  // Build a smooth path using bezier
  const path = useMemo(() => {
    if (!points.length) return "";
    let d = `M${points[0][0]},${points[0][1]}`;
    for (let i = 1; i < points.length; i++) {
      const [x1, y1] = points[i - 1];
      const [x2, y2] = points[i];
      const midX = (x1 + x2) / 2;
      d += ` Q${midX},${y1} ${midX},${(y1 + y2) / 2} T${x2},${y2}`;
    }
    return d;
  }, [points]);

  // Timeline labels
  const timeLabels = ["00:30", "02", "03", "04", "05", "06", "07", "08", "09:35"];

  // Event markers: map time label → x
  const eventsWithX = useMemo(
    () =>
      events.map((e, idx) => {
        const [hh, mm] = e.at.split(":").map(Number);
        const minutes = hh * 60 + mm;
        const startMin = 30;
        const endMin = 9 * 60 + 35;
        const t = (minutes - startMin) / (endMin - startMin);
        const x = padX + Math.max(0, Math.min(1, t)) * innerW;
        const seriesIdx = Math.round(
          Math.max(0, Math.min(1, t)) * (series.length - 1)
        );
        const y = points[seriesIdx]?.[1] || h / 2;
        return { ...e, x, y, idx };
      }),
    [events, points, series.length]
  );

  const selected = eventsWithX[selectedEventIdx];

  return (
    <div className="w-full">
      <svg viewBox={`0 0 ${w} ${h + 26}`} className="w-full h-auto">
        <defs>
          <linearGradient id="phaseStroke" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#67e8d1" />
            <stop offset="55%" stopColor="#a78bfa" />
            <stop offset="100%" stopColor="#c084fc" />
          </linearGradient>
          <filter id="glow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* guide line for selected event */}
        {selected && (
          <line
            x1={selected.x}
            x2={selected.x}
            y1={padY - 10}
            y2={h - padY / 2}
            stroke="rgba(255,255,255,0.6)"
            strokeWidth="1"
          />
        )}

        <path
          d={path}
          fill="none"
          stroke="url(#phaseStroke)"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          filter="url(#glow)"
        />

        {/* awake ticks: small white marks where v > 2.8 */}
        {series.map((v, i) =>
          v > 2.8 && i % 2 === 0 ? (
            <rect
              key={i}
              x={padX + (i / (series.length - 1)) * innerW - 0.8}
              y={padY - 6}
              width="1.6"
              height="5"
              fill="#ffffff"
              opacity="0.75"
            />
          ) : null
        )}

        {/* event dots */}
        {eventsWithX.map((e) => (
          <g
            key={e.id}
            onClick={() => onSelect && onSelect(e.idx)}
            style={{ cursor: "pointer" }}
          >
            <circle cx={e.x} cy={e.y} r="5" fill="#0a2030" />
            <circle
              cx={e.x}
              cy={e.y}
              r="3.4"
              fill={e.idx === selectedEventIdx ? "#ffffff" : "#cbd5e1"}
            />
          </g>
        ))}

        {/* time labels */}
        {timeLabels.map((l, i) => {
          const x = padX + (i / (timeLabels.length - 1)) * innerW;
          return (
            <text
              key={l}
              x={x}
              y={h + 16}
              fontSize="10"
              textAnchor="middle"
              fill="rgba(255,255,255,0.7)"
            >
              {l}
            </text>
          );
        })}
      </svg>
    </div>
  );
}

// ---------- Waveform (mocked) ----------
function Waveform({ progress = 0.35, bars = 90 }) {
  const data = useMemo(
    () =>
      Array.from({ length: bars }, () => 0.1 + Math.random() * 0.9),
    [bars]
  );
  const highlightStart = Math.floor(bars * 0.3);
  const highlightEnd = Math.floor(bars * 0.42);
  return (
    <div className="flex items-end gap-[2px] h-10 flex-1 min-w-0 overflow-hidden">
      {data.map((v, i) => {
        const inHighlight = i >= highlightStart && i <= highlightEnd;
        const past = i / bars <= progress;
        return (
          <span
            key={i}
            style={{
              height: `${v * 100}%`,
              opacity: inHighlight ? 1 : past ? 0.85 : 0.4,
            }}
            className={`w-[2px] rounded-sm ${
              inHighlight ? "bg-[#14c2a1]" : "bg-[#67e8d1]"
            }`}
          />
        );
      })}
    </div>
  );
}

// ---------- Colored 4-segment bar (basso → alto), tonalità verdi/teal dell'app ----------
function FourSegBar({ active = 0 }) {
  // Da chiaro a saturo, tutti dentro la palette teal/verde app (#00A896 = primario)
  const colors = ["#67e8d1", "#14c2a1", "#00a896", "#0a7a6b"];
  return (
    <div className="flex h-1.5 gap-1">
      {colors.map((c, i) => (
        <div
          key={i}
          className="flex-1 rounded-full transition-opacity"
          style={{
            background: c,
            opacity: i === active ? 1 : 0.32,
          }}
        />
      ))}
    </div>
  );
}

// keep older alias so existing usages still work
function ThreeBar({ active }) {
  return <FourSegBar active={active} />;
}

// ---------- Noise histogram (dB over night) ----------
function NoiseHistogram({ data = [], median = 0 }) {
  const w = 640;
  const h = 170;
  const padT = 14;
  const padB = 30;
  const padL = 8;
  const padR = 8;
  const innerW = w - padL - padR;
  const innerH = h - padT - padB;
  const maxV = Math.max(...data, 1);
  const barW = innerW / data.length - 1.5;
  const timeLabels = ["00:30", "02", "03", "04", "05", "06", "07", "08", "09:35"];

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto">
      <defs>
        <linearGradient id="nBar" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#4de3c4" />
          <stop offset="100%" stopColor="#0a8670" />
        </linearGradient>
      </defs>

      {/* baseline */}
      <line
        x1={padL}
        x2={w - padR}
        y1={padT + innerH - (median / maxV) * innerH}
        y2={padT + innerH - (median / maxV) * innerH}
        stroke="rgba(255,255,255,0.55)"
        strokeWidth="1"
      />

      {data.map((v, i) => {
        const x = padL + i * (innerW / data.length);
        const bh = (v / maxV) * innerH;
        const y = padT + innerH - bh;
        return (
          <rect
            key={i}
            x={x}
            y={y}
            width={barW}
            height={Math.max(bh, 1)}
            rx={barW / 2}
            fill="url(#nBar)"
            opacity={v > median ? 0.95 : 0.75}
          />
        );
      })}

      {timeLabels.map((l, i) => {
        const x = padL + (i / (timeLabels.length - 1)) * innerW;
        return (
          <text
            key={l}
            x={x}
            y={h - 8}
            fontSize="10"
            textAnchor="middle"
            fill="rgba(255,255,255,0.65)"
          >
            {l}
          </text>
        );
      })}
    </svg>
  );
}

export default function Riepilogo() {
  const navigate = useNavigate();
  const mic = useMic();
  const { getEntry } = useJournal();
  const clips = useAudioClips();
  const { api } = useAuth();
  const [mode, setMode] = useState("integrated"); // "mattress" | "integrated"
  const [soundIdx, setSoundIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [filter, setFilter] = useState("all");
  const [consentOpen, setConsentOpen] = useState(false);

  // ---- Dati reali dal backend ----
  const [today, setToday] = useState(null); // null=loading; {has_data,...}
  const [breathSeries, setBreathSeries] = useState([]);
  const [loadErr, setLoadErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [tRes, bRes] = await Promise.all([
          api.get("/api/user/me/today"),
          api.get("/api/user/me/today/series?sensor=breath_r&limit=2000"),
        ]);
        if (cancelled) return;
        setToday(tRes.data);
        setBreathSeries(bRes.data?.points || []);
      } catch {
        if (!cancelled) {
          setLoadErr("Impossibile caricare i dati del sonno");
          setToday({ has_data: false });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api]);

  const isLoading = today === null && !loadErr;
  const hasData = !!today?.has_data;

  // Mostra il dialog di consenso clip la prima volta che l'utente ha dato
  // il permesso microfono e non ha ancora deciso sulle clip
  useEffect(() => {
    if (mode === "integrated" && mic.permission === "granted" && clips.consent === null) {
      const t = setTimeout(() => setConsentOpen(true), 600);
      return () => clearTimeout(t);
    }
  }, [mode, mic.permission, clips.consent]);

  // Timeline audio: solo se c'è una sessione reale CON audio. Nessun mock.
  const audioTimeline = useMemo(() => {
    if (!hasData) return [];
    if (mode !== "integrated" || mic.permission !== "granted") return [];
    const supported = sleepSoundEvents.filter((e) =>
      ["snore", "cough", "talk", "breath"].includes(e.type)
    );
    // Inietto un breath_interruption di demo se manca (scenario realistico per UI)
    const hasBreath = supported.some((e) => e.type === "breath");
    if (!hasBreath) {
      supported.splice(2, 0, {
        id: "demo_breath",
        type: "breath",
        at: "02:30",
        duration_sec: 18,
        intensity: "lieve",
        has_clip: true,
        clip_id: "mock_clip_breath",
      });
    }

    const items = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    supported.slice(0, 4).forEach((e, i) => {
      const dur =
        e.duration_sec ||
        (e.type === "snore" ? 200 + i * 30 : e.type === "cough" ? 1.2 : 12);
      const tlType =
        e.type === "cough"
          ? "cough_bout"
          : e.type === "breath"
          ? "breath_interruption"
          : e.type;
      let iso;
      if (e.at && /^\d{2}:\d{2}$/.test(e.at)) {
        const [hh, mm] = e.at.split(":").map((n) => parseInt(n, 10));
        const d = new Date(today);
        d.setHours(hh, mm, 0, 0);
        iso = d.toISOString();
      } else {
        iso = e.at || new Date(Date.now() - (4 - i) * 3600 * 1000).toISOString();
      }
      items.push({
        at: iso,
        type: tlType,
        duration_sec: dur,
        intensity:
          e.intensity ||
          (tlType === "snore"
            ? "medio"
            : tlType === "breath_interruption"
            ? "lieve"
            : null),
        original_has_clip: !!e.has_clip,
        has_clip: clips.consent === true && !!e.has_clip,
        clip_id: clips.consent === true ? e.clip_id || null : null,
      });
    });
    return items;
  }, [hasData, mode, mic.permission, clips.consent]);

  const todayJournal = getEntry();

  // ---- Override mock con dati reali dal backend ----
  // sleepDayStats: se hasData arricchisci con valori reali, altrimenti EMPTY (zero/dash).
  // NIENTE dati mock se l'utente non ha sessioni.
  const sleepDayStatsLive = useMemo(() => {
    if (!hasData) return EMPTY_DAY_STATS;
    const dur = today.duration_min ?? 0;
    const score = today.score ?? null;
    return {
      ...EMPTY_DAY_STATS,
      bedtime: today.session?.started_at
        ? new Date(today.session.started_at).toTimeString().slice(0, 5)
        : "—",
      wakeup: today.session?.ended_at
        ? new Date(today.session.ended_at).toTimeString().slice(0, 5)
        : "—",
      inBed: formatHM(dur),
      asleep: formatHM(
        (today.sleep_phases || []).reduce(
          (s, p) => s + (p.name !== "WAKE" ? Number(p.minutes || 0) : 0),
          0
        ) || dur
      ),
      scoreMattress: score ?? 0,
      scoreIntegrated: score ?? 0,
      heartRateAvg: today.sensors?.heart_r?.avg ?? 0,
      date: today.session?.started_at
        ? new Date(today.session.started_at).toLocaleDateString("it-IT", {
            weekday: "long",
            day: "numeric",
            month: "short",
          })
        : "—",
    };
  }, [hasData, today]);

  // sleepPhasesSeries: converti phases_epochs (strings) in array di indici 0/1/2/3
  // attesi da PhasesChart. Subsample a max 300 punti per performance. Empty → [] (chart vuoto con assi).
  const sleepPhasesSeriesLive = useMemo(() => {
    if (!hasData) return [];
    const raw = today?.phases_epochs || [];
    if (!raw.length) return [];
    const indices = raw.map((p) => PHASE_TO_IDX[p] ?? 1);
    if (indices.length <= 300) return indices;
    const step = indices.length / 300;
    const subsampled = [];
    for (let i = 0; i < 300; i++) {
      subsampled.push(indices[Math.floor(i * step)]);
    }
    return subsampled;
  }, [hasData, today]);

  // Minuti per fase dal backend (per la legenda fasi). Empty → zeri.
  const phasesMinLive = useMemo(() => {
    if (!hasData || !today?.phases_min) return EMPTY_PHASES_MIN;
    return today.phases_min;
  }, [hasData, today]);

  // breathFrequency: solo da serie reale. Empty → median 0, label "—".
  const breathFrequencyLive = useMemo(() => {
    if (!hasData || !breathSeries.length) return EMPTY_BREATH;
    const med = median(breathSeries.map((p) => p.value));
    return {
      median: med != null ? Math.round(med * 10) / 10 : 0,
      label: "NORMALE",
      unit: "/min",
    };
  }, [hasData, breathSeries]);

  // Eventi audio rilevati (mic): solo se hasData. Niente mock per nuovi utenti.
  const soundEventsLive = useMemo(
    () => (hasData ? sleepSoundEvents : []),
    [hasData]
  );

  // Rumore ambientale: solo se hasData.
  const ambientNoiseLive = hasData ? ambientNoise : EMPTY_AMBIENT;
  const ambientNoiseSeriesLive = hasData ? ambientNoiseSeries : [];

  // Week rings: solo se hasData (per ora il backend ancora non aggrega settimana).
  const weekRingsLive = hasData ? weekOverview : EMPTY_WEEK;

  // tips: priorità ai tips reali dal backend (score_mattress + tips_engine).
  // Quando non ci sono dati o l'aggregator non ha generato tips, fallback al
  // generatore client-side basato su mock + journal.
  const tips = useMemo(() => {
    const backendTips = today?.tips || [];
    if (backendTips.length > 0) return backendTips;
    return generateTips({
      sleep: sleepDayStatsLive,
      mode,
      journal: todayJournal,
      mic: mic.permission,
    });
  }, [today, sleepDayStatsLive, mode, todayJournal, mic.permission]);

  const filteredEvents = useMemo(() => {
    if (filter === "all") return soundEventsLive;
    return soundEventsLive.filter((e) => e.type === filter);
  }, [filter, soundEventsLive]);

  const currentSound = filteredEvents[soundIdx] || soundEventsLive[0] || null;
  const [playbackHint, setPlaybackHint] = useState("");

  // Ferma sempre la riproduzione al cambio clip/unmount
  useEffect(() => {
    setPlaying(false);
    stopClip().catch(() => {});
    setPlaybackHint("");
  }, [currentSound?.id]);

  useEffect(() => {
    return () => {
      stopClip().catch(() => {});
    };
  }, []);

  /**
   * Toggle play/pause sulla clip corrente.
   * - Se il currentSound ha un clip_id reale e esiste il blob in IndexedDB →
   *   riproduzione cross-platform via clipPlayer (NativeAudio su iOS, <audio>
   *   su web).
   * - Altrimenti (mock event): mostra hint "Anteprima — clip demo, nessun
   *   audio disponibile" e non triggera riproduzione fake.
   */
  const onTogglePlay = async () => {
    if (!currentSound) return;
    if (playing) {
      await stopClip();
      setPlaying(false);
      return;
    }
    const clipId = currentSound.clip_id;
    if (!clipId) {
      setPlaybackHint("Clip demo: audio reale disponibile dopo la prima sessione registrata.");
      return;
    }
    try {
      const record = await clips.getClip(clipId);
      if (!record?.blob) {
        setPlaybackHint("Clip non trovata sul dispositivo o scaduta (retention 30 giorni).");
        return;
      }
      const res = await playClipBlob(record.blob, {
        onEnded: () => setPlaying(false),
      });
      if (!res.ok) {
        setPlaybackHint(
          res.reason === "native_error"
            ? "Impossibile riprodurre la clip su questo dispositivo."
            : "Errore di riproduzione audio."
        );
        return;
      }
      setPlaybackHint("");
      setPlaying(true);
    } catch (e) {
      setPlaybackHint("Errore lettura clip: " + String(e));
    }
  };

  const onDeleteCurrentClip = async () => {
    if (!currentSound) return;
    await stopClip();
    setPlaying(false);
    if (currentSound.clip_id) {
      try {
        await clips.deleteClip(currentSound.clip_id);
      } catch {
        /* silenzioso */
      }
    }
    // Avanza al successivo (o resta a 0 se era l'ultimo)
    setSoundIdx((i) =>
      filteredEvents.length <= 1 ? 0 : i % (filteredEvents.length - 1)
    );
  };

  const prevSound = () =>
    setSoundIdx((i) =>
      filteredEvents.length === 0
        ? 0
        : (i - 1 + filteredEvents.length) % filteredEvents.length
    );
  const nextSound = () =>
    setSoundIdx((i) =>
      filteredEvents.length === 0 ? 0 : (i + 1) % filteredEvents.length
    );

  const counts = {
    cough: soundEventsLive.filter((e) => e.type === "cough").length,
    snore: soundEventsLive.filter((e) => e.type === "snore").length,
    talk: soundEventsLive.filter((e) => e.type === "talk").length,
    move: soundEventsLive.filter((e) => e.type === "move").length,
  };

  // Card "Dati dal materasso": valori reali da backend o "—" se non disponibili
  const heartSensor = sensors.find((s) => s.id === "heartRate");
  const breathSensor = sensors.find((s) => s.id === "breathRate");
  const heartCurrent = hasData ? (today?.sensors?.heart_r?.avg ?? null) : null;
  const breathCurrent = hasData ? (today?.sensors?.breath_r?.avg ?? null) : null;
  const mattressCards = [
    {
      key: "heart",
      icon: heartSensor?.icon,
      title: heartSensor?.title || "BPM",
      subtitle: heartSensor?.subtitle || "Frequenza cardiaca",
      current: heartCurrent != null ? Math.round(heartCurrent) : null,
      route: "/sensor/heartRate",
    },
    {
      key: "breath",
      icon: breathSensor?.icon,
      title: breathSensor?.title || "ATTI/m",
      subtitle: breathSensor?.subtitle || "Frequenza respiratoria",
      current: breathCurrent != null ? Math.round(breathCurrent) : null,
      route: "/sensor/breathRate",
    },
  ];

  return (
    <div className="px-4 pt-3 pb-36">
      {/* Banner stato: NON blocca le sezioni sotto */}
      {isLoading && (
        <div
          className="mb-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-2.5 flex items-center text-white/70 text-[12.5px] gap-2"
          data-testid="riepilogo-loading"
        >
          <Loader2 size={14} className="animate-spin" />
          Caricamento riepilogo notte…
        </div>
      )}

      {!isLoading && !hasData && (
        <div
          className="mb-3 rounded-2xl border border-[#14c2a1]/25 bg-[#14c2a1]/10 px-4 py-3 text-[12.5px] flex items-start gap-2"
          data-testid="riepilogo-empty"
        >
          <Moon size={14} className="mt-0.5 text-[#67e8d1]" />
          <div className="flex-1">
            <div className="font-semibold text-white">
              Connetti il tuo SmartDream per vedere i dati reali
            </div>
            <div className="text-white/70 mt-0.5">
              {loadErr
                ? `${loadErr}. Verifica la connessione.`
                : "Nessuna sessione registrata. Le sezioni qui sotto si popoleranno automaticamente dopo la prima notte di monitoraggio."}
            </div>
          </div>
          <button
            onClick={() => navigate("/bedtime")}
            className="text-[#67e8d1] hover:text-white text-[12px] font-medium flex items-center gap-0.5 whitespace-nowrap"
          >
            Avvia <ChevronRight size={12} />
          </button>
        </div>
      )}

      {/* Sezioni SEMPRE visibili */}
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-white text-[34px] leading-none font-semibold tracking-tight">
            {sleepDayStatsLive.date.split(" ")[0]}
          </h1>
          <div className="text-white/60 text-[13px] mt-1">
            {sleepDayStatsLive.date.split(" ").slice(1).join(" ")}
          </div>
        </div>
        <button
          onClick={() => {}}
          className="relative w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white hover:bg-white/10 transition-colors"
          aria-label="calendario"
        >
          <Calendar size={16} />
          <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-rose-400 ring-2 ring-[#2b3134]" />
        </button>
      </div>

      {/* Week rings */}
      <div className="mt-4 flex items-center justify-between gap-1.5">
        {weekRingsLive.map((d, i) => (
          <DayRing
            key={i}
            d={d.d}
            quality={d.quality}
            active={!!d.active}
          />
        ))}
        <button className="w-11 h-11 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:bg-white/10 transition-colors">
          <BedDouble size={16} />
        </button>
      </div>

      {/* Mode switcher: Solo materasso vs Integrato (materasso + audio) */}
      <div className="mt-5 relative grid grid-cols-2 p-1 bg-white/5 border border-white/10 rounded-2xl">
        <button
          onClick={() => setMode("mattress")}
          className={`relative z-10 py-2.5 text-[13px] font-medium rounded-xl flex items-center justify-center gap-1.5 transition-colors ${
            mode === "mattress" ? "text-white" : "text-white/60"
          }`}
        >
          <BedDouble size={14} /> Solo materasso
        </button>
        <button
          onClick={() => setMode("integrated")}
          className={`relative z-10 py-2.5 text-[13px] font-medium rounded-xl flex items-center justify-center gap-1.5 transition-colors ${
            mode === "integrated" ? "text-white" : "text-white/60"
          }`}
        >
          <AudioLines size={14} /> Integrato
          {mic.permission !== "granted" && (
            <span className="ml-1 w-1.5 h-1.5 rounded-full bg-amber-300" />
          )}
        </button>
        <div
          className="absolute top-1 bottom-1 w-[calc(50%-4px)] rounded-xl transition-all"
          style={{
            left: mode === "mattress" ? 4 : "calc(50% + 0px)",
            background:
              "linear-gradient(155deg, #12b096 0%, #0a8670 100%)",
            boxShadow: "0 6px 18px -8px rgba(20,194,161,0.55)",
          }}
        />
      </div>

      <div className="mt-2 text-white/60 text-[12px] leading-snug">
        {mode === "mattress"
          ? "Analisi basata solo sui sensori del materasso (battito, respiro, temperatura)."
          : mic.permission === "granted"
          ? "Analisi combinata: sensori del materasso + microfono (tosse, russare, rumori)."
          : "Per l'analisi completa, attiva il microfono."}
      </div>

      {/* Quality + time */}
      <section className="mt-4 flex items-center justify-between gap-3">
        <QualityGauge
          value={
            mode === "mattress"
              ? sleepDayStatsLive.scoreMattress
              : sleepDayStatsLive.scoreIntegrated
          }
        />
        <div className="flex-1 pl-2">
          <button className="w-full text-left block">
            <div className="text-white text-[22px] font-semibold leading-none tracking-tight">
              {sleepDayStatsLive.inBed}
            </div>
            <div className="text-white/65 text-[13px] mt-1 flex items-center gap-1">
              A letto <ChevronRight size={12} />
            </div>
          </button>
          <div className="h-3" />
          <button className="w-full text-left block">
            <div className="text-white text-[22px] font-semibold leading-none tracking-tight">
              {sleepDayStatsLive.asleep}
            </div>
            <div className="text-white/65 text-[13px] mt-1 flex items-center gap-1">
              Addormentato <ChevronRight size={12} />
            </div>
          </button>
        </div>
        <button
          className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white hover:bg-white/10 transition-colors"
          aria-label="esporta"
        >
          <Upload size={16} />
        </button>
      </section>

      {/* Tabs: Fasi / Suoni / Cuore */}
      <section className="mt-6">
        <div className="flex items-center gap-2">
          <SegmentedPill
            icon={<Moon size={14} />}
            label="Fasi del sonno"
            active
          />
          <span className="relative">
            <SegmentedPill
              icon={<Activity size={14} className="text-[#14c2a1]" />}
              compact
            />
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-400 text-white text-[10px] font-semibold flex items-center justify-center ring-2 ring-[#2b3134]">
              {counts.cough}
            </span>
          </span>
          <SegmentedPill
            icon={<Heart size={14} className="text-[#14c2a1]" />}
            compact
          />
        </div>

        {/* Phases chart */}
        <div className="mt-4 flex items-center justify-between">
          <h3 className="text-white text-[18px] font-semibold flex items-center gap-2">
            Fasi del sonno
            <InfoPoint keyName="algo_staging" />
          </h3>
          <div className="flex items-center gap-1.5 text-white/70 text-[12px]">
            {mode === "integrated" ? (
              mic.permission === "granted" ? (
                <>
                  <Mic size={12} className="text-[#14c2a1]" /> Mic + Materasso
                </>
              ) : (
                <>
                  <MicOff size={12} /> Solo materasso
                </>
              )
            ) : (
              <>
                <BedDouble size={12} /> Solo materasso
              </>
            )}
          </div>
        </div>

        <div className="mt-2 rounded-2xl bg-white/[0.03] border border-white/5 p-2">
          <PhasesChart
            series={sleepPhasesSeriesLive}
            events={mode === "integrated" ? soundEventsLive : []}
            selectedEventIdx={Math.min(
              soundIdx,
              Math.max(0, soundEventsLive.length - 1)
            )}
            onSelect={(i) => setSoundIdx(i)}
          />
        </div>

        {/* Phase legend */}
        <div className="mt-3 grid grid-cols-2 gap-y-1.5 text-[13px]">
          <LegendItem color="#ffffff" label="Veglia" value={formatHM(phasesMinLive.awake_min)} />
          <LegendItem color="#c084fc" label="Sogno" value={formatHM(phasesMinLive.rem_min)} />
          <LegendItem color="#67e8d1" label="Leggero" value={formatHM(phasesMinLive.light_min)} />
          <LegendItem color="#0a8670" label="Profondo" value={formatHM(phasesMinLive.deep_min)} />
        </div>
      </section>

      {/* Audio Timeline (riascoltabile) */}
      {mode === "integrated" && mic.permission === "granted" && audioTimeline.length > 0 && (
        <section className="mt-7">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-white text-[20px] font-semibold tracking-tight flex items-center gap-2">
              Momenti significativi della notte
              <InfoPoint keyName="sensor_mic" />
            </h3>
            {clips.consent === null && (
              <button
                onClick={() => setConsentOpen(true)}
                className="text-[12px] text-[#67e8d1] hover:text-white underline underline-offset-2"
                data-testid="enable-clips-cta"
              >
                Abilita riascolto
              </button>
            )}
            {clips.consent === false && (
              <button
                onClick={() => setConsentOpen(true)}
                className="text-[12px] text-white/60 hover:text-white underline underline-offset-2"
                data-testid="enable-clips-cta"
              >
                Modifica consenso
              </button>
            )}
          </div>
          <p className="text-white/55 text-[12.5px] mb-3">
            {clips.consent === true
              ? "Tocca ▶ per riascoltare le clip. Salvate solo sul tuo dispositivo."
              : clips.consent === false
              ? "Riascolto clip disattivato. Il rilevamento eventi resta attivo."
              : "Vuoi riascoltare questi momenti? Richiede consenso esplicito."}
          </p>
          <AudioTimeline events={audioTimeline} />
        </section>
      )}

      {/* Sounds player (integrated mode only) */}
      {mode === "integrated" && (
      <section className="mt-7">
        <h3 className="text-white text-[20px] font-semibold tracking-tight flex items-center gap-2">
          Registrazioni notturne
          <InfoPoint keyName="sensor_mic" />
        </h3>
        <p className="text-white/60 text-[13px] mt-1">
          Clip rilevate automaticamente dal microfono.
        </p>

        {mic.permission !== "granted" ? (
          <div className="mt-3 rounded-2xl border border-amber-400/25 bg-amber-400/[0.06] p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-400/15 border border-amber-400/30 flex items-center justify-center text-amber-300">
                <MicOff size={16} />
              </div>
              <div className="flex-1">
                <div className="text-white text-[14px] font-medium">
                  Microfono non attivo
                </div>
                <div className="text-white/70 text-[12px] mt-0.5">
                  Attiva il microfono per registrare tosse, russare e suoni
                  notturni.
                </div>
                <button
                  onClick={() => mic.request()}
                  className="mt-3 h-9 px-4 rounded-full bg-[#14c2a1] text-[#0b3a34] text-[13px] font-semibold hover:brightness-110 transition-all"
                >
                  Attiva microfono
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Filter pills */}
            <div className="mt-3 flex items-center gap-2 overflow-x-auto no-scrollbar pb-1">
              <FilterChip
                active={filter === "all"}
                onClick={() => {
                  setFilter("all");
                  setSoundIdx(0);
                }}
                label={`Tutti (${soundEventsLive.length})`}
                icon={<Activity size={12} />}
              />
              <FilterChip
                active={filter === "cough"}
                onClick={() => {
                  setFilter("cough");
                  setSoundIdx(0);
                }}
                label={`Tosse (${counts.cough})`}
              />
              <FilterChip
                active={filter === "snore"}
                onClick={() => {
                  setFilter("snore");
                  setSoundIdx(0);
                }}
                label={`Russare (${counts.snore})`}
              />
              <FilterChip
                active={filter === "talk"}
                onClick={() => {
                  setFilter("talk");
                  setSoundIdx(0);
                }}
                label={`Parlato (${counts.talk})`}
              />
            </div>

            {/* Player card */}
            {currentSound ? (
              <div className="mt-3 rounded-2xl bg-white/[0.04] border border-white/10 p-4 overflow-hidden" data-testid="audio-player-card">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Activity size={16} className="text-[#14c2a1]" />
                    <span className="text-white text-[15px] font-semibold">
                      {currentSound.label}
                    </span>
                    <span className="text-[#14c2a1] text-[14px] font-semibold">
                      {soundIdx + 1}/{filteredEvents.length}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={prevSound}
                      className="w-8 h-8 rounded-full hover:bg-white/10 text-white/85 flex items-center justify-center transition-colors"
                    >
                      <SkipBack size={14} />
                    </button>
                    <button
                      onClick={nextSound}
                      className="w-8 h-8 rounded-full hover:bg-white/10 text-white/85 flex items-center justify-center transition-colors"
                    >
                      <SkipForward size={14} />
                    </button>
                  </div>
                </div>

                <div className="mt-2 text-white/70 text-[12.5px]">
                  {currentSound.at} — ({currentSound.label})
                </div>

                <div className="mt-3 flex items-center gap-3">
                  <button
                    onClick={onTogglePlay}
                    data-testid="audio-play-btn"
                    className="w-10 h-10 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 text-[#14c2a1] flex items-center justify-center transition-colors"
                  >
                    {playing ? <Pause size={16} /> : <Play size={16} />}
                  </button>
                  <Waveform progress={playing ? 0.5 : 0.3} />
                </div>

                {playbackHint && (
                  <div
                    className="mt-2 text-[11.5px] text-amber-200/80 flex items-start gap-1.5"
                    data-testid="audio-playback-hint"
                  >
                    <AlertCircle size={12} className="mt-0.5 shrink-0" />
                    <span>{playbackHint}</span>
                  </div>
                )}
              </div>
            ) : (
              <div
                className="mt-3 rounded-2xl bg-white/[0.03] border border-white/10 px-4 py-6 text-center"
                data-testid="recordings-empty"
              >
                <Activity size={18} className="mx-auto text-white/40" />
                <div className="text-white/70 text-[13.5px] mt-2 font-medium">
                  Nessuna registrazione disponibile
                </div>
                <div className="text-white/50 text-[12px] mt-1">
                  Le clip notturne appariranno qui dopo la prima sessione.
                </div>
              </div>
            )}
          </>
        )}
      </section>
      )}

      {/* Heart rate avg */}
      <section className="mt-6 rounded-2xl bg-white/5 border border-white/10 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 rounded-full bg-rose-400" />
          <span className="text-white/90 text-[14px] flex items-center gap-1.5">
            Frequenza cardiaca media
            <InfoPoint keyName="hr_avg" />
          </span>
        </div>
        <div className="text-white font-semibold" data-testid="riepilogo-hr-avg">
          {sleepDayStatsLive.heartRateAvg > 0
            ? `${sleepDayStatsLive.heartRateAvg} bpm`
            : "— bpm"}
        </div>
      </section>

      {/* Stat grid */}
      <section className="mt-4 grid grid-cols-2 gap-x-5 gap-y-4">
        <Stat
          icon={<Activity size={18} className="text-[#67e8d1]" />}
          value={`${sleepDayStatsLive.regularity}%`}
          label="Regolarità"
        />
        <Stat
          icon={<Timer size={18} className="text-[#67e8d1]" />}
          value={sleepDayStatsLive.fellAsleepIn}
          label="Addormentato dopo"
        />
        <Stat
          icon={<Moon size={18} className="text-[#67e8d1]" />}
          value={sleepDayStatsLive.bedtime}
          label="Coricato"
        />
        <Stat
          icon={<AlarmClock size={18} className="text-[#67e8d1]" />}
          value={sleepDayStatsLive.wakeup}
          label="Svegliato"
        />
        <Stat
          icon={<Wind size={18} className="text-[#67e8d1]" />}
          value={sleepDayStatsLive.snoring}
          label="Il mio russare"
        />
        <Stat
          icon={<BedDouble size={18} className="text-[#67e8d1]" />}
          value={`${sleepDayStatsLive.clips.done}/${sleepDayStatsLive.clips.total}`}
          label="clip completate"
        />
        <Stat
          icon={<Smile size={18} className="text-[#67e8d1]" />}
          value={sleepDayStatsLive.mood}
          label="Umore al risveglio"
        />
        <Stat
          icon={<HeartPulse size={18} className="text-[#67e8d1]" />}
          value={`${sleepDayStatsLive.heartRateAvg}`}
          label="Battito cardiaco"
        />
      </section>

      <button
        onClick={() => navigate("/diario")}
        className="mt-5 w-full h-11 rounded-full border border-white/20 text-white/90 text-[14px] font-medium hover:bg-white/5 transition-colors flex items-center justify-center gap-2"
      >
        <Plus size={14} /> {todayJournal ? "Modifica diario di oggi" : "Aggiungi appunti sul sonno"}
      </button>

      {/* Consigli personalizzati: solo se ci sono dati reali dalla sessione.
          Senza dati non ha senso mostrare consigli (sarebbero del generatore
          mock based-on-journal e potrebbero confondere l'utente). */}
      <section className="mt-7">
        <div className="flex items-center justify-between">
          <h3 className="text-white text-[20px] font-semibold tracking-tight flex items-center gap-2">
            <Lightbulb size={18} className="text-[#67e8d1]" />
            Consigli per te
            <InfoPoint keyName="algo_tips" />
          </h3>
          {hasData && !todayJournal && (
            <button
              onClick={() => navigate("/diario")}
              className="text-[#67e8d1] text-[12px] font-medium flex items-center gap-1"
            >
              Compila il diario <ChevronRight size={12} />
            </button>
          )}
        </div>
        {hasData ? (
          <>
            <p className="text-white/60 text-[13px] mt-1">
              Basati sui tuoi dati di stanotte
              {todayJournal ? " e sul diario di oggi" : ""}.
            </p>
            <div className="mt-3 space-y-3">
              {tips.slice(0, 5).map((t, i) => (
                <TipCard key={i} tip={t} />
              ))}
              {tips.length === 0 && (
                <div className="text-white/60 text-sm">
                  Nessun consiglio per oggi, continua così!
                </div>
              )}
            </div>
          </>
        ) : (
          <div
            className="mt-3 rounded-2xl border border-[#14c2a1]/25 bg-[#14c2a1]/10 px-4 py-4 text-center"
            data-testid="tips-empty-state"
          >
            <Lightbulb size={18} className="mx-auto text-[#67e8d1]" />
            <div className="mt-2 text-white text-[14px] font-medium">
              Connetti SmartDream per ricevere consigli personalizzati
            </div>
            <div className="mt-1 text-white/65 text-[12px]">
              I consigli si attivano dopo la prima notte di monitoraggio.
            </div>
          </div>
        )}
      </section>

      {/* Audio-based stat cards (integrated mode only) */}
      {mode === "integrated" && (
      <section className="mt-4 grid grid-cols-1 gap-3">
        {/* Tosse */}
        <BigStatCard
          title="Tosse"
          icon={<Activity size={16} className="text-[#67e8d1]" />}
          value={sleepDayStatsLive.coughPerHour}
          suffix="/h"
          subLabel="Media"
          levelLabel={sleepDayStatsLive.coughLabel}
          activeSeg={0}
        />

        {/* Interruzioni del respiro */}
        <BigStatCard
          title="Interruzioni del respiro"
          icon={<Wind size={16} className="text-[#67e8d1]" />}
          value={sleepDayStatsLive.breathInterruptionsPerHour}
          suffix="/h"
          subLabel="Media"
          levelLabel={sleepDayStatsLive.breathInterruptionsLabel}
          activeSeg={0}
        />

        {/* Rumore ambientale */}
        <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-4">
          <div className="flex items-center justify-between">
            <div className="text-white text-[15px] font-medium">
              Rumore ambientale
            </div>
            <AudioLines size={16} className="text-[#67e8d1]" />
          </div>
          <div className="mt-3 flex items-end justify-between gap-4">
            <div>
              <div className="text-white text-[38px] leading-none font-semibold tracking-tight">
                {ambientNoiseLive.median}
                <span className="text-[14px] opacity-70 ml-0.5">
                  {ambientNoiseLive.unit}
                </span>
              </div>
              <div className="text-white/60 text-[12px] mt-1">Mediana</div>
            </div>
            <div className="flex-1 text-right">
              <div className="text-white/80 text-[13px] uppercase tracking-wider">
                {ambientNoiseLive.label}
              </div>
              <div className="mt-2">
                <FourSegBar active={0} />
              </div>
            </div>
          </div>
          <div className="mt-3 rounded-xl bg-[#0a1a17] border border-white/5 p-2">
            <NoiseHistogram
              data={ambientNoiseSeriesLive}
              median={ambientNoiseLive.median}
            />
          </div>
        </div>

        {/* Frequenza respiratoria */}
        <BigStatCard
          title="Frequenza respiratoria"
          icon={<Waves size={16} className="text-[#67e8d1]" />}
          value={breathFrequencyLive.median}
          suffix={breathFrequencyLive.unit}
          subLabel="Mediana"
          levelLabel={breathFrequencyLive.label}
          activeSeg={1}
        />
      </section>
      )}

      {/* Mattress sensors quick */}
      <section className="mt-6">
        <div className="flex items-center justify-between">
          <h3 className="text-white text-[18px] font-semibold">
            Dati dal materasso
          </h3>
          <button
            onClick={() => navigate("/")}
            className="text-white/65 text-[12px] flex items-center gap-1"
          >
            Vedi tutti <ChevronRight size={12} />
          </button>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3">
          {mattressCards.map((s) => (
            <button
              key={s.key}
              onClick={() => navigate(s.route)}
              data-testid={`mattress-card-${s.key}`}
              className="rounded-2xl p-3 text-left transition-transform hover:-translate-y-0.5"
              style={{
                background:
                  "linear-gradient(155deg, #10a78c 0%, #0a8672 100%)",
                boxShadow: "0 8px 22px -14px rgba(0,168,138,0.55)",
              }}
            >
              <div className="flex items-center justify-between">
                <div className="w-8 h-8 rounded-xl bg-white/15 border border-white/20 flex items-center justify-center">
                  <SensorIcon name={s.icon} size={16} className="text-white" />
                </div>
                <ChevronRight size={14} className="text-white/70" />
              </div>
              <div className="mt-3 text-white text-[24px] font-semibold leading-none">
                {s.current != null ? s.current : "—"}
                <span className="text-xs opacity-80 ml-1">{s.title}</span>
              </div>
              <div className="text-white/85 text-[12px] mt-1">{s.subtitle}</div>
            </button>
          ))}
        </div>
      </section>

      <ClipConsentDialog open={consentOpen} onClose={() => setConsentOpen(false)} />
    </div>
  );
}

// ---- Small components ----
function SegmentedPill({ icon, label, active, compact }) {
  return (
    <div
      className={`flex items-center gap-2 rounded-full transition-all ${
        compact ? "w-10 h-10 justify-center" : "h-10 px-4"
      } ${
        active
          ? "bg-white text-[#0a2030]"
          : "bg-white/10 text-white/90 border border-white/10"
      }`}
    >
      {icon}
      {label && <span className="text-[13px] font-medium">{label}</span>}
    </div>
  );
}

function LegendItem({ color, label, value }) {
  return (
    <div className="flex items-center gap-2">
      <span
        className="w-2.5 h-2.5 rounded-full"
        style={{ backgroundColor: color }}
      />
      <span className="text-white/80 text-[13px]">{label}</span>
      <span className="text-white text-[13px] font-medium ml-1">{value}</span>
    </div>
  );
}

function FilterChip({ active, onClick, label, icon }) {
  return (
    <button
      onClick={onClick}
      className={`whitespace-nowrap h-8 px-3 rounded-full text-[12.5px] font-medium transition-all flex items-center gap-1.5 ${
        active
          ? "bg-white text-[#0a2030]"
          : "bg-white/5 text-white/85 border border-white/10 hover:bg-white/10"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function IconBtn({ children, danger }) {
  return (
    <button
      className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
        danger
          ? "bg-rose-400/10 text-rose-300 hover:bg-rose-400/20"
          : "bg-white/5 text-[#67e8d1] hover:bg-white/10 border border-white/10"
      }`}
    >
      {children}
    </button>
  );
}

function Stat({ icon, value, label }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-xl bg-[#14c2a1]/10 border border-[#14c2a1]/20 flex items-center justify-center">
        {icon}
      </div>
      <div>
        <div className="text-white text-[22px] leading-none font-semibold tracking-tight">
          {value}
        </div>
        <div className="text-white/65 text-[12px] mt-0.5">{label}</div>
      </div>
    </div>
  );
}


function BigStatCard({
  title,
  icon,
  value,
  suffix,
  subLabel,
  levelLabel,
  activeSeg = 0,
}) {
  return (
    <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-4">
      <div className="flex items-center justify-between">
        <div className="text-white text-[15px] font-medium">{title}</div>
        {icon}
      </div>
      <div className="mt-3 flex items-end justify-between gap-4">
        <div>
          <div className="text-white text-[38px] leading-none font-semibold tracking-tight">
            {value}
            <span className="text-[14px] opacity-70 ml-0.5">{suffix}</span>
          </div>
          <div className="text-white/60 text-[12px] mt-1">{subLabel}</div>
        </div>
        <div className="flex-1 text-right">
          <div className="text-white/80 text-[13px] uppercase tracking-wider">
            {levelLabel}
          </div>
          <div className="mt-2">
            <FourSegBar active={activeSeg} />
          </div>
        </div>
      </div>
    </div>
  );
}

const TIP_ICON_MAP = {
  moon: Moon,
  timer: Timer,
  heart: HeartPulse,
  volume: Volume2,
  activity: Activity,
  wind: Wind,
  alarm: AlarmClock,
  coffee: Coffee,
  wine: Wine,
  brain: Brain,
  dumbbell: Dumbbell,
  utensils: BedDouble,
  notebook: Notebook,
  sparkles: Sparkles,
};

const PRIORITY_COLORS = {
  high: {
    ring: "border-rose-400/40",
    bg: "bg-rose-400/10",
    icon: "text-rose-300",
    badge: "bg-rose-400/20 text-rose-200 border-rose-400/30",
    label: "Da migliorare",
  },
  medium: {
    ring: "border-amber-400/40",
    bg: "bg-amber-400/10",
    icon: "text-amber-200",
    badge: "bg-amber-400/20 text-amber-100 border-amber-400/30",
    label: "Da tenere d'occhio",
  },
  low: {
    ring: "border-[#14c2a1]/40",
    bg: "bg-[#14c2a1]/10",
    icon: "text-[#67e8d1]",
    badge: "bg-[#14c2a1]/20 text-[#67e8d1] border-[#14c2a1]/30",
    label: "Consiglio",
  },
  info: {
    ring: "border-[#14c2a1]/40",
    bg: "bg-[#14c2a1]/15",
    icon: "text-[#67e8d1]",
    badge: "bg-[#14c2a1]/25 text-white border-[#14c2a1]/40",
    label: "Ottimo",
  },
};

function TipCard({ tip }) {
  const Icon = TIP_ICON_MAP[tip.icon] || Lightbulb;
  const p = PRIORITY_COLORS[tip.priority] || PRIORITY_COLORS.low;
  return (
    <div
      className={`rounded-2xl border bg-white/[0.04] ${p.ring} p-3.5 flex items-start gap-3`}
    >
      <div
        className={`w-10 h-10 rounded-xl ${p.bg} ${p.icon} flex items-center justify-center flex-shrink-0`}
      >
        <Icon size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${p.badge}`}
          >
            {p.label}
          </span>
          <span className="text-white/60 text-[11px]">· {tip.category}</span>
        </div>
        <div className="text-white text-[15px] font-semibold mt-1 leading-snug">
          {tip.title}
        </div>
        <p className="text-white/75 text-[13px] mt-1 leading-snug">
          {tip.description}
        </p>
      </div>
    </div>
  );
}

